// src/modules/app-views/_apps/appsindico/unitProfiles/pages/Document/index.jsx
// *******************************************************************************
// UNIT PROFILES DOCUMENT
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { FormOutlined } from '@ant-design/icons';
import UnitProfilesRecordTab from './tabs/RecordTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;
const TAB_LABEL_STYLE = { display: 'flex', alignItems: 'center', gap: 6 };

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const UnitProfilesDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();

  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['name'];

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  // Build tabs
  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      viewMode,
      data,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const profileId = values?._id || allValues?._id || data?._id;
      const dataToSave = { ...values };

      await upsert({ dataSet: dataToSave, _id: profileId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="name"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          locked={viewMode === 'view'}
          showInfoMessage={true}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <SimpleLoadingArea
            name="cardHeader"
            offsetTop={0}
            showProgress={false}
            zIndex={100}
            borderRadius={8}
          />

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default UnitProfilesDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab } = props;

  return [
    {
      key: initTab,
      label: (
        <span style={TAB_LABEL_STYLE}>
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <UnitProfilesRecordTab {...props} tabName={initTab} />,
    },
  ];
}
